import { Service } from '../../../store/service/types';
import { Service as ServiceData } from '../../../store/data/types';
import { Button, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TextField, Typography } from '@mui/material';
import UpTransition from '../../../shared/UpTransition/UpTransition';
import { createIconFromName } from '../../../util/iconUtil';
import { useEffect, useState } from 'react';
import parameterSelector, { SelectedValueType } from '../AddServiceDialog/parameter/parameterSelector';
import useComplexObject from '../AddServiceDialog/parameter/useComplexObject';
import ErrorTypography from '../../../shared/ErrorTypography/ErrorTypography';
import { ParameterValidatorService } from '../AddServiceDialog/parameter/parameterValidatorService';
import { Trans, useTranslation } from 'react-i18next';
import { INode, SelectionT } from 'react-digraph';
import { nameTranslator } from '../../../locales/NameTranslator';

type Props = {
  service: Service | null;
  selected: SelectionT;
  onClose: (success: boolean, name: string, data: Record<string, SelectedValueType>) => void;
  serviceValues: ServiceData | undefined;
};

const EditServiceDialog = ({ service, selected, onClose, serviceValues }: Props): JSX.Element => {
  const { t } = useTranslation();

  
  const [name, setName] = useState('' as string);
  const [parameters, updateParameters, resetParameters, setParameters] = useComplexObject<SelectedValueType>({});
  const [error, setError] = useState(false);

  const reset = () => {
    resetParameters();
    setName('');
    setError(false);
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    reset();
    if(selected.nodes && selected.nodes.values().next().value.title) {
      setName(selected?.nodes?.values().next().value.title);
    }
    if(service) {
      const oldValues: { [key: string] : SelectedValueType } = { };
      service.parameters.map(parameter => {
        if(serviceValues && serviceValues.parameters[parameter.parameter]) {
          switch (parameter.type) {
            case "enum": {
              oldValues[parameter.parameter] = parseInt(serviceValues?.parameters[parameter.parameter]);
              if(Number.isNaN(oldValues[parameter.parameter])) {
                oldValues[parameter.parameter] = serviceValues?.parameters[parameter.parameter];
              }
              break;
            }
            case "int": {
              oldValues[parameter.parameter] = parseInt(serviceValues?.parameters[parameter.parameter]);
              break;
            }
            case "double": {
              oldValues[parameter.parameter] = parseFloat(serviceValues?.parameters[parameter.parameter]);
              break;
            }
          }
        }
      })
      setParameters(oldValues);
    }
  }, [service]);

  const handleClose = (success: boolean) => () => { //TODO: if data wasn't chanched "Editservice" closes window without error
    if (success) {
      const validationResult = name && ParameterValidatorService.validateAll(service?.parameters ?? [], parameters);
      if (!validationResult) {

        setError(true);
        return;
      }
    }
    onClose(success, name, parameters);
    reset();
  };

  const handleParameterChange = (parameter: string) => (value: SelectedValueType) => updateParameters(parameter, value);

  return (
    <Dialog
      open={service !== null && selected.nodes !== null && selected.nodes.size === 1}
      onClose={handleClose(false)}
      aria-labelledby={'edit-service-dialog-title'}
      aria-describedby={'edit-service-dialog-description'}
      fullWidth={true}
      maxWidth={'md'}
      TransitionComponent={UpTransition}
    >
      {service !== null && selected.nodes !== null && selected.nodes.size === 1 && (
        <>
          <DialogTitle id={'edit-service-dialog-title'}>
            {createIconFromName(service.icon)} Service <i><b>{nameTranslator(service)}</b></i> ändern
          </DialogTitle>
          <DialogContent>
            <TextField
              variant={'filled'}
              label={t('Name')}
              defaultValue={selected.nodes.values().next().value.title}
              onChange={event => setName(event.target.value)}
              fullWidth
            />
            {service.parameters.length > 0 && (
              <>
                <Typography variant={'h6'} sx={{ my: 3 }}>
                  <Trans>Parameter</Trans>
                </Typography>
                <Stack spacing={1}>
                  {service.parameters.map((parameter, i) => {
                    const Component = parameterSelector(parameter.type);
                    return (
                      <Component
                        key={i}
                        {...parameter}
                        onChange={handleParameterChange(parameter.parameter)}
                        selected={parameters[parameter.parameter] ?? (serviceValues?.parameters[parameter.parameter] ?? undefined)}
                      />
                    );
                  })}
                </Stack>
              </>
            )}
            <Typography variant={'h6'} sx={{ my: 3 }}>
              <Trans>Expected Inputs</Trans>&nbsp;{service.inputs}
            </Typography>
            
            {error && <ErrorTypography message={'Bitte Angaben überprüfen'} />}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose(false)}>
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleClose(true)}>
              <Trans>Apply Changes</Trans>
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default EditServiceDialog;